import {
  Component,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { NzFormDirective } from 'ng-zorro-antd/form';
import { NUMBER_FILTER_OPERATORS } from '../../../constants/filters.constants';
import { FormControlDynamicComponent, SelectOption } from '@form';
import { TranslocoDirective } from '@jsverse/transloco';
import { ButtonComponent } from '../../atoms/button/button.component';
import { IconComponent } from '../../atoms/icon/icon.component';
import {
  FilterConfig,
  FilterFormGroup,
  FilterFormValue,
} from '../../../models/filters.models';
import { FiltersService } from '../../../services/filters.service';

@Component({
  selector: 'ui-filters-form',
  templateUrl: 'filters-form.component.html',
  imports: [
    ReactiveFormsModule,
    NzFormDirective,
    ButtonComponent,
    FormControlDynamicComponent,
    IconComponent,
    TranslocoDirective,
  ],
})
export class FiltersFormComponent {
  private fb = inject(FormBuilder);
  private filtersService = inject(FiltersService);

  public readonly numberFilterOperators = signal<SelectOption[]>([]);
  public readonly defaultFilters = signal<FilterConfig[]>([]);
  private readonly VALUE_CTRL_KEY = 'value';
  private readonly FIELD_CTRL_KEY = 'field';
  private readonly CONDITION_CTRL_KEY = 'condition';

  typesFilterOperators = signal<any[]>([]);
  optionsFilter = input<any>();
  filters = signal<FilterConfig[]>([]);
  form: FormGroup = this.fb.group({
    filters: this.fb.array<FilterFormGroup>([]),
  });
  filtersArray: FormArray = this.form.get('filters') as FormArray;
  onApplyFilters = output<FilterFormValue[]>();

  constructor() {
    // Efecto que se ejecuta cuando `optionsFilter` cambia
    effect(() => {
      this.filtersService
        .localizeNumberFilterOperators(NUMBER_FILTER_OPERATORS)
        .subscribe((localized) => {
          this.numberFilterOperators.set(localized);
        });
      this.filtersService
        .localizeDefaultFilters(this.optionsFilter())
        .subscribe((localized) => {
          this.defaultFilters.set(localized);
          this.filters.set(localized);
        });
      this.filtersService
        .localizeTypesFilterCategory(this.optionsFilter())
        .subscribe((localized) => {
          this.typesFilterOperators.set(localized);
        });
      this.filtersService.buildFiltersArrayValueChanges(
        this.filtersArray,
        this.defaultFilters(),
        (updatedFilters) => this.filters.set(updatedFilters),
      );
    });
  }

  getTypesFilterOperators(field: string) {
    return this.typesFilterOperators().filter((op) => op.value === field);
  }

  addFilter() {
    const filterGroup = this.filtersService.createFilterFormGroup();
    this.filtersArray.push(filterGroup);
  }

  onChangeFilterField(filterId: string, index: number) {
    const filters = this.filters;
    const filter = filters().find((filter) => filter.value === filterId);
    const filterGroup = this.filtersArray.at(index) as FormGroup;

    if (!filter) {
      console.error(`No filter founded for ${filterId}`);
      return;
    }

    this.filtersService.updateFilterGroup(filter, filterGroup);
    this.filtersService.resetValueControl(filterGroup, this.VALUE_CTRL_KEY);
  }

  getFieldControl(group: AbstractControl) {
    return group.get(this.FIELD_CTRL_KEY) as FormControl;
  }

  getConditionControl(group: AbstractControl) {
    return group.get(this.CONDITION_CTRL_KEY) as FormControl;
  }

  getValueControl(group: AbstractControl) {
    return group.get(this.VALUE_CTRL_KEY) as FormControl;
  }

  getFilterOption(filterOptionId: string) {
    return this.filters().find((filter) => filter.value === filterOptionId);
  }

  removeItem(itemIndex: number) {
    this.filtersArray.removeAt(itemIndex);
  }

  removeFilters() {
    this.filtersArray.clear();
    this.onApplyFilters.emit([]);
  }

  getFilterTypeInputs(option: string) {
    const filterOptions = this.getFilterOption(option);
    return {
      options:
        this.filtersService.getLocalizedOptions(filterOptions?.options) || [],
      suffix: filterOptions?.suffix,
      min: filterOptions?.min,
      max: filterOptions?.max,
      subType: filterOptions?.subType,
    };
  }

  applyFilters() {
    let filterSelectGroup: any[] = [];
    const infoFilter = this.form.value.filters.filter((value: any) => {
      if (value.type !== 'selectGroup') {
        return value;
      } else {
        filterSelectGroup.push(value);
      }
    });

    let filters: FilterFormValue[] =
      this.filtersService.mapFormValues(infoFilter);
    filterSelectGroup.map((filter) => {
      filters = filter.value.split('-').map((item: string, index: number) => {
        return {
          name: filter.field.split('-')[index],
          operator: filter.condition,
          value: item,
        };
      });
    });

    this.onApplyFilters.emit(filters);
  }
}
